import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import type { SelectOption } from "@/lib/options"
import { capitalize, formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import { IconCheck, IconDots, IconEye, IconPencil, IconPlus, IconSearch, IconX } from "@tabler/icons-react"
import { useState } from "react"

interface Employee {
  id: number
  name: string
  avatar_url: string | null
}

interface Bill {
  id: number
  bill_number: string
  display_name: string
  bill_type_display: string
  billing_frequency_display: string
  bill_date: string
  date_paid: string | null
  formatted_total: string
  status: string
  sent_to_accounting: boolean
  can_update: boolean
}

// Keyed by status value, plus "all". The keys come from Bill::STATUSES, so they
// are not spelled out here.
type StatusCounts = Record<string, number>

interface Props {
  [key: string]: unknown
  bills: Bill[]
  pagy: PagyMeta
  status_counts: StatusCounts
  statuses: SelectOption[]
  filters: {
    search?: string
    status?: string
    bill_type?: string
    currency?: string
    user_id?: string
    start_date?: string
    end_date?: string
  }
  employees: Employee[]
  bill_types: SelectOption[]
  can_create: boolean
}

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  paid: "default",
  pending: "secondary",
  draft: "outline",
  overdue: "destructive",
  cancelled: "outline",
}

const FREQ_VARIANTS: Record<string, "default" | "secondary" | "outline"> = {
  monthly: "default",
  yearly: "secondary",
  one_time: "outline",
}

export default function BillsIndex() {
  const { bills, pagy, status_counts, statuses, filters, employees, bill_types, can_create } = usePage<Props>().props

  const statusFilters: { label: string; value?: string; count: number }[] = [
    { label: "All", value: undefined, count: status_counts.all ?? 0 },
    ...statuses.map((s) => ({ label: s.label, value: s.value, count: status_counts[s.value] ?? 0 })),
  ]

  const [search, setSearch] = useState(filters.search || "")

  function applyFilters(overrides: Record<string, string | undefined>) {
    const params: Record<string, string | undefined> = {
      search: filters.search,
      status: filters.status,
      bill_type: filters.bill_type,
      currency: filters.currency,
      user_id: filters.user_id,
      start_date: filters.start_date,
      end_date: filters.end_date,
      ...overrides,
    }
    const cleaned = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ""))
    router.get("/bills", cleaned, { preserveState: true })
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    applyFilters({ search: search || undefined })
  }

  function clearFilters() {
    setSearch("")
    router.get("/bills", {}, { preserveState: true })
  }

  function handleFilter(status?: string) {
    applyFilters({ status })
  }

  const hasFilters =
    filters.search ||
    filters.status ||
    filters.bill_type ||
    filters.currency ||
    filters.user_id ||
    filters.start_date ||
    filters.end_date

  return (
    <>
      <Head title="Bills - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Bills</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Bills</h1>
          {can_create && (
            <div className="flex gap-2">
              <Button size="sm" asChild>
                <a href="/bills/new">
                  <IconPlus className="mr-1 h-4 w-4" />
                  New Bill
                </a>
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <form onSubmit={handleSearch} className="flex gap-2 lg:max-w-sm">
            <div className="relative flex-1">
              <IconSearch className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search bills..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline">
              Search
            </Button>
          </form>

          <div className="flex flex-wrap items-center gap-2">
            <Select value={filters.bill_type || ""} onValueChange={(v) => applyFilters({ bill_type: v || undefined })}>
              <SelectTrigger size="sm">
                <SelectValue placeholder="Bill Type" />
              </SelectTrigger>
              <SelectContent>
                {bill_types.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.user_id || ""} onValueChange={(v) => applyFilters({ user_id: v || undefined })}>
              <SelectTrigger size="sm">
                <SelectValue placeholder="Employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={String(e.id)}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <IconX className="mr-1 h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground hidden text-sm sm:inline">Status:</span>
          {statusFilters.map((filter) => (
            <Button
              key={filter.label}
              variant={filters.status === filter.value ? "default" : "outline"}
              size="sm"
              onClick={() => handleFilter(filter.value)}
            >
              {filter.label} ({filter.count})
            </Button>
          ))}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bill #</TableHead>
              <TableHead>Vendor / Employee</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead>Bill Date</TableHead>
              <TableHead>Date Paid</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Accounting</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bills.map((bill) => (
              <TableRow key={bill.id}>
                <TableCell className="font-medium">
                  <a href={`/bills/${bill.id}`} className="hover:underline">
                    {bill.bill_number}
                  </a>
                </TableCell>
                <TableCell>{bill.display_name}</TableCell>
                <TableCell>{bill.bill_type_display}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      FREQ_VARIANTS[bill.billing_frequency_display.toLowerCase().replace(/ /g, "_")] || "outline"
                    }
                  >
                    {bill.billing_frequency_display}
                  </Badge>
                </TableCell>
                <TableCell>{formatDate(bill.bill_date)}</TableCell>
                <TableCell>{formatDate(bill.date_paid)}</TableCell>
                <TableCell className="text-right font-medium">{bill.formatted_total}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANTS[bill.status] || "outline"}>{capitalize(bill.status)}</Badge>
                </TableCell>
                <TableCell className="text-center">
                  {bill.sent_to_accounting ? (
                    <IconCheck className="text-green-600 dark:text-green-400 mx-auto h-4 w-4" />
                  ) : (
                    <IconX className="text-muted-foreground mx-auto h-4 w-4" />
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <IconDots className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <a href={`/bills/${bill.id}`}>
                          <IconEye className="mr-2 h-4 w-4" />
                          View
                        </a>
                      </DropdownMenuItem>
                      {bill.can_update && (
                        <DropdownMenuItem asChild>
                          <a href={`/bills/${bill.id}/edit`}>
                            <IconPencil className="mr-2 h-4 w-4" />
                            Edit
                          </a>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {bills.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-muted-foreground text-center">
                  No bills found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

BillsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
